import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ButtonComponent } from '@shared/components/button';
import { FieldHintDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { ModalFooterDirective, ModalShellComponent } from '@shared/components/modal';
import { TooltipDirective } from '@shared/directives/tooltip';
import { ConnexionAnnuaire, InformationsBaseDeDonnees, LONGUEURS_MAXIMALES } from '../../liste-clients.interface';

export interface DialogModifierBaseDeDonneesData {
  nomConnectionString: string;
  informations?: InformationsBaseDeDonnees;
  connexionAnnuaire?: ConnexionAnnuaire;
}

export interface DialogModifierBaseDeDonneesOutput {
  serveur: string;
  baseDeDonnees: string;
  utilisateur?: string;
  motDePasse?: string;
  motDePasseDepuisAnnuaire?: boolean;
}

@Component({
  selector: 'app-dialog-modifier-base-de-donnees',
  imports: [
    ModalShellComponent,
    ModalFooterDirective,
    ButtonComponent,
    FormFieldComponent,
    FieldHintDirective,
    InputComponent,
    TooltipDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './dialog-modifier-base-de-donnees.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './dialog-modifier-base-de-donnees.component.scss',
})
export class DialogModifierBaseDeDonneesComponent {
  // Bornes de saisie partagées avec le backend : une valeur trop longue serait refusée,
  // et surtout la modification ne pourrait pas être enregistrée dans le journal d'audit
  protected readonly longueurs = LONGUEURS_MAXIMALES;

  readonly dialogRef =
    inject<DialogRef<DialogModifierBaseDeDonneesOutput, DialogModifierBaseDeDonneesComponent>>(DialogRef);
  readonly data = inject<DialogModifierBaseDeDonneesData>(DIALOG_DATA);

  // Reprise annuaire : le mot de passe legacy sera lu côté serveur au moment de la
  // sauvegarde — il ne transite jamais par le navigateur
  motDePasseDepuisAnnuaire = signal<boolean>(false);

  baseDeDonneesForm = new FormGroup({
    serveur: new FormControl<string>(this.data.informations?.serveur ?? '', { nonNullable: true }),
    baseDeDonnees: new FormControl<string>(this.data.informations?.baseDeDonnees ?? '', { nonNullable: true }),
    utilisateur: new FormControl<string>(this.data.informations?.utilisateur ?? '', { nonNullable: true }),
    motDePasse: new FormControl<string>('', { nonNullable: true }),
  });

  // Miroir signal du formulaire, lu par le bouton de validation
  private readonly valeurs = toSignal(
    this.baseDeDonneesForm.valueChanges.pipe(map(() => this.baseDeDonneesForm.getRawValue())),
    { initialValue: this.baseDeDonneesForm.getRawValue() },
  );

  readonly peutValider = computed(() => !!this.valeurs().serveur && !!this.valeurs().baseDeDonnees);

  constructor() {
    // Un mot de passe saisi manuellement est prioritaire sur la reprise annuaire
    this.baseDeDonneesForm.controls.motDePasse.valueChanges.subscribe(motDePasse => {
      if (motDePasse) {
        this.motDePasseDepuisAnnuaire.set(false);
      }
    });
  }

  annuler(): void {
    this.dialogRef.close();
  }

  reprendreAnnuaire() {
    const connexion = this.data.connexionAnnuaire;
    if (!connexion) {
      return;
    }

    this.baseDeDonneesForm.patchValue({
      serveur: connexion.serveur ?? '',
      baseDeDonnees: connexion.baseDeDonnees ?? '',
      utilisateur: connexion.utilisateur ?? '',
      motDePasse: '',
    });
    this.motDePasseDepuisAnnuaire.set(connexion.motDePassePresent);
  }

  valider() {
    if (!this.peutValider()) {
      return;
    }

    this.dialogRef.close({
      serveur: this.baseDeDonneesForm.controls.serveur.value,
      baseDeDonnees: this.baseDeDonneesForm.controls.baseDeDonnees.value,
      utilisateur: this.baseDeDonneesForm.controls.utilisateur.value || undefined,
      motDePasse: this.baseDeDonneesForm.controls.motDePasse.value || undefined,
      motDePasseDepuisAnnuaire: this.motDePasseDepuisAnnuaire() || undefined,
    });
  }
}
